import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ContractsWeeksPerMonthConfigurationOptionEnum,
  SetLegalEntityWeeksPerMonthConfigurationDocument,
  WeeksPerMonthConfigurationFragment,
} from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'
import omitBy from 'lodash/omitBy'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import SelectField from 'modules/core/components/SelectField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'
import app from 'modules/core/store/models/app'

type Props = {
  weeksPerMonthConfiguration: WeeksPerMonthConfigurationFragment | undefined
  legalEntity: LegalEntity
}

interface FormValues {
  weeksPerMonth: string
}

const WeeksPerMonthConfigurationSelector = ({ legalEntity, weeksPerMonthConfiguration }: Props) => {
  const weeksPerMonthExtents: Record<string, string> = {
    days_in_a_year_based: i18n.t(
      'settings.legal_entities.legal_entity.contracts.weeks_per_month.options.days_in_a_year_based_extent'
    ),
    weeks_in_a_year_based: i18n.t(
      'settings.legal_entities.legal_entity.contracts.weeks_per_month.options.weeks_in_a_year_based_extent'
    ),
  }

  const weeksPerMonthOptions = [
    {
      label: i18n.t(
        'settings.legal_entities.legal_entity.contracts.weeks_per_month.options.days_in_a_year_based'
      ),
      value: 'days_in_a_year_based',
    },
    {
      label: i18n.t(
        'settings.legal_entities.legal_entity.contracts.weeks_per_month.options.weeks_in_a_year_based'
      ),
      value: 'weeks_in_a_year_based',
    },
  ]

  const [upsert, {}] = useMutation(SetLegalEntityWeeksPerMonthConfigurationDocument, {
    extractorFn: (data) => data.contractsMutations.upsertContractsWeeksPerMonthConfiguration,
  })

  const form = useForm<FormValues>({
    enableReinitialize: true,
    entity: weeksPerMonthConfiguration,
    defaultValues: {
      weeksPerMonth: 'days_in_a_year_based',
    },
    onSubmit: async (data, {}) => {
      const result = await upsert({
        variables: {
          ...omitBy(data, isEmpty),
          weeksPerMonth: data.weeksPerMonth as ContractsWeeksPerMonthConfigurationOptionEnum,
          legalEntityId: legalEntity.id,
        },
      })

      const handled = handleResult(result, ({}) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      })

      if (handled.success && handled.data.weeksPerMonthConfiguration) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  return (
    <Form
      onSubmit={() => {
        form.handleSubmit()
      }}
    >
      <FormRow>
        <SelectField
          label={i18n.t('settings.legal_entities.legal_entity.contracts.weeks_per_month.title')}
          value={form.values.weeksPerMonth}
          options={weeksPerMonthOptions}
          onChange={(value) => form.setValues({ ...form.values, weeksPerMonth: value })}
        />
        <Box paddingTop="s8" paddingRight="s4" textAlign="right">
          <Text.Normal>{weeksPerMonthExtents[form.values.weeksPerMonth]}</Text.Normal>
        </Box>
      </FormRow>
      <FormRow footer>
        <Button disabled={form.isSubmitting || !form.isValid} submit>
          {form.isSubmitting ? i18n.t('common.saving') : i18n.t('common.submit')}
        </Button>
      </FormRow>
    </Form>
  )
}

export default WeeksPerMonthConfigurationSelector
